import { useMemo, useState } from 'react';
import type { Command, Tsa } from '@integrate/contracts';
import {
  FTE,
  HEADCOUNT,
  computeStageBridge,
  computeTsaEconomics,
  countsInHeadcount,
  currentBucket,
  bucketMetaFor,
  fmt,
  fmtMoney,
  severanceCostOf,
  tsaReplacements,
  waveOf,
  type Person,
  type SeparationState,
} from '@integrate/core';
import { TsaEconomics } from './TsaEconomics.js';
import { TsaTimeline } from './TsaTimeline.js';
import { BuildUpForm } from './TransfersPanel.js';
import { LiveField } from './live-input.js';
import { Workbench } from '../ui/Workbench.js';
import { StatTile } from '../ui/StatTile.js';
import { ICON, IconButton } from '../ui/Icon.js';
import { Section } from './sections.js';

/**
 * The TSA & Day 2 phase panel, ported from `p4PanelHtml` / `wireP4Panel` (orgchart.html:6342–6400).
 *
 * Day 1 is not the finish line. The carve-out starts life buying services it cannot yet run — payroll,
 * IT, treasury — from the company it just left, under transitional service agreements with an end date.
 * This phase is about what happens when those end: every TSA is a service the receiving side has to
 * build or buy before the clock runs out, and every Day 2 exit is a Day 1 position that was only ever
 * temporary.
 *
 * The panel itself is a summary and two doors: the register, and the rightsizing list. The decisions are
 * made per person and per agreement, not here.
 */
export function TsaPanel({
  state,
  readOnly,
  scenarioId,
  onDispatch,
  onOpenRegister,
  onOpenAssumptions,
  onNext,
}: {
  state: SeparationState;
  readOnly: boolean;
  scenarioId: string;
  onDispatch(command: Command | readonly Command[]): Promise<boolean>;
  onOpenRegister(): void;
  onOpenAssumptions(): void;
  onNext(): void;
}): JSX.Element | null {
  const hc = useMemo(() => computeStageBridge(state, { fn: HEADCOUNT }), [state]);
  const fte = useMemo(() => computeStageBridge(state, { fn: FTE }), [state]);
  // Raw salary, matching the caption. The loaded multiplier belongs to the economics surfaces.
  const cost = useMemo(
    () => computeStageBridge(state, { fn: (p) => Number(p.salary) || 0 }),
    [state],
  );

  const tsas = state.design.tsas;
  /*
    Both counts, from the engine that decides them.

    `matched` used to be `tsaReplacements(...).length > 0` — the raw linked count — while the timeline and
    the economics pane one click away report `covered`. So a TSA whose only replacement was an unfilled
    requisition, or one carrying no needed-by month, read as matched in this tile and as uncovered on both
    surfaces that price it. Naming both populations is the only honest tile: linked is a plan, covered is a
    plan that works.
  */
  const econ = useMemo(() => computeTsaEconomics(state), [state]);
  const matched = econ.perTsa.filter((x) => x.replFilled.length > 0).length;
  const uncovered = econ.uncovered;
  const exitMonths = tsas.filter((t) => t.durationMonths > 0).map((t) => t.durationMonths);
  // Counted, not dropped: `exitMonths` excludes them, so without this the range and the total disagree.
  const noTermN = tsas.filter((t) => t.durationMonths <= 0).length;

  /*
    The rightsizing pool: everybody flagged to leave the carve-out again after Day 1.

    Two populations with two different storage places — an employee's flag lives on their decision row,
    a build-up's on the build-up itself — and only DAY 1 build-ups can be rightsized, because a Day 2
    hire has not arrived by the time the exits happen.
  */
  const exiters: Person[] = useMemo(() => {
    const fromRoster = state.employees.filter(
      (e) =>
        bucketMetaFor(state.design, e.id)?.day2Exit === true &&
        currentBucket(state.design, e.id) === 'Carve-out',
    );
    const fromBuildUps = state.design.buildUps.filter(
      (b) => b.day2Exit && waveOf(b) === 'day1' && currentBucket(state.design, b.id) === 'Carve-out',
    );
    return [...fromRoster, ...fromBuildUps];
  }, [state]);

  const counted = exiters.filter(countsInHeadcount);
  const exitFte = counted.reduce((a, e) => a + (Number(e.fte) || 0), 0);
  const exitCost = counted.reduce((a, e) => a + (Number(e.salary) || 0), 0);
  // Employees only: a cancelled build-up position has no incumbent to pay off.
  const exitSeverance = counted
    .filter((e) => !e.isBuildUp)
    .reduce((a, e) => a + severanceCostOf(e, state.assumptions), 0);

  const keep = (person: Person): void => {
    /*
      Two different commands for the same word on screen, because the flag lives in two places. A
      build-up's Day 2 exit is a property of the planned position; an employee's is part of their
      decision row. Sending the wrong one is a silent no-op that still writes an event.
    */
    void onDispatch(
      person.isBuildUp
        ? { type: 'updateBuildUp', scenarioId, buildUpId: person.id, patch: { day2Exit: false } }
        : {
            type: 'setDay2Exit',
            scenarioId,
            assignments: [{ employeeId: person.id, day2Exit: false }],
          },
    );
  };

  if (!state.employees.length) return null;

  return (
    <>
      <Section
        testId="day2-strip"
        title="Day 2 organisation"
      >
        <div className="pp-stats">
          <StatTile label="Day 1" value={fmt(hc.day1)} sub={`${fmt(fte.day1, 1)} FTE`} />
          <StatTile label="− Day 2 exits" value={fmt(hc.rightsizing)} sub="rightsizing" testId="day2-exits" />
          <StatTile
            label="+ TSA repl. / D2"
            value={fmt(hc.tsaRepl + hc.d2BuildUps)}
            sub={`${fmt(hc.tsaRepl)} TSA · ${fmt(hc.d2BuildUps)} other`}
          />
          <StatTile
            label="= Day 2"
            value={fmt(hc.day2)}
            sub={`${fmt(fte.day2, 1)} FTE · ${fmtMoney(cost.day2)}`}
            testId="day2-total"
          />
        </div>
        <p className="muted">
          Cost = raw annual salaries (the loaded multiplier applies to the economics surfaces only).
        </p>
      </Section>

      <Section
        testId="tsa-summary"
        title={
          <>
            TSA register <span className="pill">{fmt(tsas.length)}</span>
          </>
        }
      >
        <div className="sp-row">
          <span className="k">TSAs registered</span>
          <span className="v" data-testid="tsa-count">
            {fmt(tsas.length)}
          </span>
        </div>
        <div className="sp-row">
          <span className="k">With a replacement in place</span>
          <span className="v" data-testid="tsa-matched">
            {fmt(matched)}/{fmt(tsas.length)}
          </span>
        </div>
        <div className="sp-row">
          <span className="k">Exits not covered</span>
          <span className={`v${uncovered ? ' bad' : ''}`} data-testid="tsa-uncovered">
            {fmt(uncovered)}
          </span>
        </div>
        {exitMonths.length > 0 && (
          <div className="sp-row">
            <span className="k">Exits (months after Day 1)</span>
            <span className="v" data-testid="tsa-exit-range">
              M{Math.min(...exitMonths)} – M{Math.max(...exitMonths)}
            </span>
          </div>
        )}
        {noTermN > 0 && (
          <p className="muted" data-testid="tsa-no-term">
            {fmt(noTermN)} of these {noTermN === 1 ? 'has' : 'have'} no agreed term, so{' '}
            {noTermN === 1 ? 'it is' : 'they are'} counted above but{' '}
            {exitMonths.length > 0 ? 'not in the range' : 'have no range'} — the timeline draws{' '}
            {noTermN === 1 ? 'it' : 'them'} open-ended.
          </p>
        )}
        <button
          className="primary"
          style={{ width: '100%', marginTop: 8 }}
          data-testid="open-tsa-register"
          onClick={onOpenRegister}
        >
          Open TSA register &amp; timeline…
        </button>
        <p className="muted">
          <strong>In place</strong> means somebody is in the job; <strong>covered</strong> means that
          person is also due before the service ends. The register holds the agreements, the exit
          timeline shows which exits are real, and the economics tab prices fee against replacement at
          loaded cost = salary ×{' '}
          <button type="button" className="linkish" data-testid="asm-open-tsa" onClick={onOpenAssumptions}>
            {Number(state.assumptions.loadedCostMultiplier) || 1}
          </button>
          .
        </p>
      </Section>

      <Section
        testId="rightsizing"
        title={
          <>
            Day 2 exits (rightsizing) <span className="pill">{fmt(exiters.length)}</span>
          </>
        }
      >
        {/*
          Same disclosure as the transfers list, for the same reason: the tile is a bridge figure and
          excludes open positions, inactive people and leavers, while this list has no such filter
          because it is the only place a mark can be removed. Hiding a row would make it unreachable.
        */}
        {exiters.length !== hc.rightsizing && (
          <p className="muted" data-testid="exit-count-gap">
            {fmt(Math.abs(exiters.length - hc.rightsizing))} of these are open positions, inactive
            people or leavers — listed here so the mark can be removed, but not counted in the tile
            above.
          </p>
        )}
        {exiters.length === 0 ? (
          <p className="muted">
            No Day 2 exits marked. Use &ldquo;Exits at Day 2 (rightsizing)&rdquo; in a person&rsquo;s
            panel.
          </p>
        ) : (
          <>
            {exiters.slice(0, EXITER_CAP).map((e) => (
              <div className="sp-row" key={e.id}>
                <span className="k grow">
                  {e.name}
                  {e.isBuildUp && <span className="pill">BU</span>}
                </span>
                <span className="v">
                  {fmtMoney(Number(e.salary) || 0)}
                  <IconButton
                    icon={ICON.remove}
                    testId={`keep-after-day2-${e.id}`}
                    label="Keep after Day 2 (remove the rightsizing mark)"
                    disabled={readOnly}
                    onClick={() => keep(e)}
                  />
                </span>
              </div>
            ))}
            {exiters.length > EXITER_CAP && (
              <p className="muted">+ {fmt(exiters.length - EXITER_CAP)} more…</p>
            )}
            <div className="sp-row">
              <span className="k">Total (raw salaries)</span>
              <span className="v">
                {fmt(exitFte, 1)} FTE · {fmtMoney(exitCost)}
              </span>
            </div>
            {/* The original's threshold, not a rounding of zero: a sub-cent total is nothing to report. */}
            {exitSeverance > 0.004 && (
              <div
                className="sp-row"
                title="Months × monthly base salary, per the severance assumptions"
              >
                <span className="k">
                  One-off severance —{' '}
                  <button type="button" className="linkish" data-testid="asm-open-tsa-severance" onClick={onOpenAssumptions}>
                    assumptions
                  </button>
                </span>
                <span className="v" data-testid="exit-severance">
                  {fmtMoney(exitSeverance)}
                </span>
              </div>
            )}
          </>
        )}
      </Section>

      <button className="primary" style={{ width: '100%' }} data-testid="tsa-next" onClick={onNext}>
        Next: RemainCo &amp; stranded cost →
      </button>
    </>
  );
}

const EXITER_CAP = 40;

/**
 * The next default name for a new agreement: one past the HIGHEST number in use, not the count.
 *
 * `TSA ${tsas.length + 1}` collides the moment anything is deleted. Three agreements named TSA 1, 2 and 3;
 * delete TSA 2; the next one is `length + 1` = 3, and the register holds two rows called "TSA 3". They are
 * distinct objects with distinct ids, so nothing breaks — it is simply a register in which the reader
 * cannot tell two rows apart, on a list whose whole job is to be read down and priced.
 *
 * Only names of the exact default shape count. Somebody who renamed one to "Payroll" has said the number
 * is not what identifies it, and skipping a number because of that would be reading their rename as data.
 *
 * The number is a unique LABEL and not a claim about position, which is the honest reading given the
 * register is ordered by when rows were created and a clone or an import can collide those wholesale.
 */
export function nextTsaName(tsas: readonly { name: string }[]): string {
  let highest = 0;
  for (const tsa of tsas) {
    const match = /^TSA (\d+)$/.exec(tsa.name);
    if (match) highest = Math.max(highest, Number(match[1]));
  }
  return `TSA ${highest + 1}`;
}

/** The wire caps a duration at an integer 0–120 months; clamping here keeps a typo from being a refusal. */
function clampMonths(raw: string): number {
  return Math.max(0, Math.min(120, Math.round(Number(raw) || 0)));
}

/**
 * An exit month, clamped to the term it belongs to.
 *
 * `billedMonths` already takes `min(duration, exited)`, so a month past the end bills the whole term
 * either way — but storing 20 against a twelve-month agreement puts a number on screen that no figure
 * anywhere agrees with, and it stays wrong after somebody lengthens the term. Clamped on the way in,
 * the register and the money always say the same thing.
 */
function clampExit(raw: string, durationMonths: number): number {
  return Math.max(0, Math.min(clampMonths(String(durationMonths)), Math.round(Number(raw) || 0)));
}


/* ------------------------------------------------------------------ */

/**
 * The TSA register, ported from `renderTsaRegisterPane` (orgchart.html:6611).
 *
 * A table of agreements, edited in place. Each cell commits on the DOM `change` event — blur or Enter,
 * not per keystroke — so typing a service description is one command rather than thirty.
 *
 * The direction column is the one that carries a rule rather than a value: **the receiving side builds.**
 * A received TSA (RemainCo → carve-out) ends and the carve-out must have hired by then; a provided one
 * (the reverse) ends and RemainCo must have. That decides which side a replacement build-up has to be on
 * for it to count, which is why changing a direction can silently orphan a replacement — the register
 * shows the match count so it is visible.
 */
export type TsaTab = 'register' | 'economics';

export function TsaRegister({
  state,
  readOnly,
  scenarioId,
  tab,
  onTab,
  onDispatch,
  onOpenAssumptions,
  onClose,
}: {
  state: SeparationState;
  readOnly: boolean;
  scenarioId: string;
  /*
    Which tab is open is per-user view state with no inverse, so it is held by the caller in component
    state — never in `lens` (it narrows nothing) and never in the preferences row (it is not a preference,
    it is where you happen to be). The app remembers it across opens within a session and forgets it on
    reload, which is the right shape and the one this gets by living in `App`.
  */
  tab: TsaTab;
  onTab(tab: TsaTab): void;
  onDispatch(command: Command | readonly Command[]): Promise<boolean>;
  /** For the replacement form, which prices a hire and links to the assumptions that do it. */
  onOpenAssumptions(): void;
  onClose(): void;
}): JSX.Element {
  /** The agreement whose replacement is being created, or null. */
  const [replacementFor, setReplacementFor] = useState<string | null>(null);

  const tsas = state.design.tsas;
  const lvl1 = state.groupingLevels[0];
  const areas = useMemo(() => {
    if (!lvl1) return [];
    const seen = new Set<string>();
    for (const e of state.employees) {
      const value = String(e.levels?.[lvl1.key] ?? '').trim();
      if (value) seen.add(value);
    }
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [state, lvl1]);

  const patch = (tsaId: string, field: string, value: string | number | null): void => {
    void onDispatch({ type: 'updateTsa', scenarioId, tsaId, patch: { [field]: value } });
  };

  const add = (): void => {
    /*
      The id is minted here because `addTsa` carries the whole object. The prefix and the default fields
      are the original's (`:6529`); a suffix from the current time plus a random tail is enough for a
      register that holds tens of rows, and the server does not care what it says as long as it is new.
    */
    const tsa: Tsa = {
      id: `tsa-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`,
      name: nextTsaName(tsas),
      service: null,
      area: null,
      direction: 'received',
      durationMonths: 12,
      monthlyCost: 0,
      status: 'planned',
      // No exit month until somebody records one; see `billedMonths`.
      exitedAtMonth: null,
      note: null,
    };
    void onDispatch({ type: 'addTsa', scenarioId, tsa });
  };

  return (
    <Workbench
      testId="tsa-register"
      title="TSA register — transitional services & exit timeline"
      onClose={onClose}
      footer={
        <>
          <button
            data-testid="tsa-add"
            disabled={readOnly || tab === 'economics'}
            onClick={add}
            title={tab === 'economics' ? 'Switch to the register to add an agreement' : undefined}
          >
            ＋ Add TSA
          </button>
          <span className="spacer" />
          <button onClick={onClose}>Done</button>
        </>
      }
    >
      <div className="tabs" role="tablist" aria-label="TSA views">
        {([
          ['register', 'Register & timeline'],
          ['economics', 'Economics'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            data-testid={`tsa-tab-${key}`}
            onClick={() => onTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'economics' ? (
        <TsaEconomics state={state} />
      ) : (
        <>
      <p className="muted">
        Services the carve-out still <strong>receives from</strong> RemainCo after Day 1 (⇢) — or{' '}
        <strong>provides to</strong> it (⇠, reverse TSA) — with duration and fee. Match replacement
        build-ups to each TSA to plan the exits; the <strong>receiving side builds</strong>: ⇢
        received → a Carve-out Day 2 hire, ⇠ provided → a Remain hire. The register belongs to the
        active scenario.
      </p>

      <datalist id="tsa-area-list">
        {areas.map((a) => (
          <option key={a} value={a} />
        ))}
      </datalist>

      {tsas.length === 0 ? (
        <p className="muted" data-testid="tsa-empty">
          No TSAs registered yet — add the services the carve-out cannot run stand-alone on Day 1.
        </p>
      ) : (
        <div className="table-scroll">
          <table className="tsa-table">
            <thead>
              <tr>
                <th>TSA</th>
                <th>Service</th>
                <th>Area</th>
                <th>Direction</th>
                <th>Months</th>
                <th>€ / month</th>
                <th>Status</th>
                <th title="The month an exited agreement actually stopped. Blank bills nothing, which is what an exit has meant until now; a month bills up to it.">
                  Exited
                </th>
                <th className="num">Repl.</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tsas.map((t) => {
                const replacements = tsaReplacements(state.design, t.id);
                return (
                  <tr key={t.id} data-testid={`tsa-row-${t.id}`}>
                    <td>
                      <CommitCell
                        testId={`tsa-name-${t.id}`}
                        value={t.name}
                        disabled={readOnly}
                        onCommit={(v) => patch(t.id, 'name', v)}
                      />
                    </td>
                    <td>
                      <CommitCell
                        testId={`tsa-service-${t.id}`}
                        value={t.service ?? ''}
                        placeholder="Service description"
                        disabled={readOnly}
                        onCommit={(v) => patch(t.id, 'service', v)}
                      />
                    </td>
                    <td>
                      <CommitCell
                        testId={`tsa-area-${t.id}`}
                        value={t.area ?? ''}
                        placeholder="Function"
                        list="tsa-area-list"
                        disabled={readOnly}
                        onCommit={(v) => patch(t.id, 'area', v)}
                      />
                    </td>
                    <td>
                      <select
                        data-testid={`tsa-direction-${t.id}`}
                        value={t.direction}
                        disabled={readOnly}
                        onChange={(e) => patch(t.id, 'direction', e.target.value)}
                      >
                        <option value="received">⇢ Received</option>
                        <option value="provided">⇠ Provided</option>
                      </select>
                    </td>
                    <td>
                      <CommitCell
                        testId={`tsa-months-${t.id}`}
                        value={String(t.durationMonths)}
                        numeric
                        min={0}
                        max={120}
                        step={1}
                        disabled={readOnly}
                        onCommit={(v) => patch(t.id, 'durationMonths', clampMonths(v))}
                      />
                    </td>
                    <td>
                      <CommitCell
                        testId={`tsa-fee-${t.id}`}
                        value={String(t.monthlyCost)}
                        numeric
                        min={0}
                        step={1000}
                        disabled={readOnly}
                        onCommit={(v) => patch(t.id, 'monthlyCost', Math.max(0, Number(v) || 0))}
                      />
                    </td>
                    <td>
                      <select
                        data-testid={`tsa-status-${t.id}`}
                        value={t.status}
                        disabled={readOnly}
                        onChange={(e) => patch(t.id, 'status', e.target.value)}
                      >
                        {(['planned', 'agreed', 'exited'] as const).map((s) => (
                          <option key={s} value={s}>
                            {s[0]!.toUpperCase() + s.slice(1)}
                          </option>
                        ))}
                      </select>
                    </td>
                    {/*
                      The month, and only where it is read.

                      `billedMonths` looks at this field for an EXITED agreement and nowhere else, so
                      an input on a planned one would be a control that changes no figure — the thing
                      this milestone keeps finding and removing. A stored month survives a status
                      flipped away and back (nothing clears it), which is why the cell reappears
                      carrying what it had rather than empty.

                      Blank is a value here, not a typo, so this cell opts out of the numeric guard
                      below: blank means no month recorded, which bills nothing, which is exactly what
                      an exit meant before the field existed and what every imported legacy file says.
                    */}
                    <td>
                      {t.status === 'exited' ? (
                        <CommitCell
                          testId={`tsa-exited-${t.id}`}
                          value={t.exitedAtMonth == null ? '' : String(t.exitedAtMonth)}
                          numeric
                          blankIsNull
                          placeholder="—"
                          min={0}
                          max={t.durationMonths}
                          step={1}
                          disabled={readOnly}
                          onCommit={(v) =>
                            patch(
                              t.id,
                              'exitedAtMonth',
                              v.trim() === '' ? null : clampExit(v, t.durationMonths),
                            )
                          }
                        />
                      ) : (
                        <span
                          className="muted"
                          data-testid={`tsa-exited-${t.id}`}
                          title="Only an exited agreement has a month it stopped in."
                        >
                          —
                        </span>
                      )}
                    </td>
                    <td
                      className="num"
                      title={
                        replacements.map((b) => b.name).join(', ') ||
                        'No replacement build-up linked yet'
                      }
                    >
                      {replacements.length ? `${replacements.length} ◆` : '—'}
                    </td>
                    <td>
                      <IconButton
                        icon={ICON.remove}
                        testId={`tsa-delete-${t.id}`}
                        label="Remove this TSA (replacement links are cleared)"
                        disabled={readOnly}
                        onClick={() => void onDispatch({ type: 'deleteTsa', scenarioId, tsaId: t.id })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/*
        The timeline sits under the table it is drawn from, as it does in the app (`:6643`): its two
        inputs are the Months and Direction cells directly above, and its markers are needed-by months
        edited one person at a time. This is the only place all those gaps are visible at once, and it
        belongs next to the fields that cause them.
      */}
      <TsaTimeline
        state={state}
        onAddReplacement={readOnly ? undefined : (tsaId) => setReplacementFor(tsaId)}
      />
        </>
      )}
      {/*
        The replacement picker is a CHILD of this dialog, not a sibling of it.

        It was a sibling of `.modal-body` until the register adopted the shared modal, and the move
        is not cosmetic: a dialog traps focus, so a picker rendered outside this one's content was
        unreachable by keyboard the moment the trap existed. Nesting it makes the two a stack —
        Escape dismisses the picker first and leaves the register open, which is what somebody
        adding a replacement for a single TSA means by it. Nothing moves on screen: the picker
        draws its own fixed-position backdrop, and `overflow: auto` on this body does not clip a
        fixed descendant.
      */}
      {replacementFor !== null && (
        <BuildUpForm
          state={state}
          scenarioId={scenarioId}
          onDispatch={onDispatch}
          /* Nothing to select from here: this panel is not the chart's selection surface. */
          onSelect={() => {}}
          onOpenAssumptions={onOpenAssumptions}
          initialTsaId={replacementFor}
          onClose={() => setReplacementFor(null)}
        />
      )}
    </Workbench>
  );
}

/**
 * A table cell that commits on `change`, not on every keystroke.
 *
 * Each commit is a command and an entry in the audit trail, so typing a service description must not
 * write one per character. React normalises `onChange` onto the DOM `input` event and offers no hook for
 * the real one, so the text is held locally and committed on blur or Enter — the same shape the
 * threshold controls use, for the same reason.
 *
 * Resynced by `useLiveInput` rather than by `key={value}`. The key was the register's original pattern and
 * the assumptions editor copied it, so both carried the same defect: a key change is a REMOUNT, and a
 * colleague editing the same cell destroyed the input you were typing in, mid-sentence and with no notice.
 * See `live-input.tsx` for why blur then needs a baseline.
 */
function CommitCell({
  testId,
  value,
  numeric,
  blankIsNull,
  placeholder,
  list,
  min,
  max,
  step,
  disabled,
  onCommit,
}: {
  testId: string;
  value: string;
  numeric?: boolean;
  /** For a nullable numeric cell, where emptying it IS the edit rather than a keystroke on the way past. */
  blankIsNull?: boolean;
  placeholder?: string;
  list?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  onCommit(value: string): void;
}): JSX.Element {
  return (
    <LiveField
      data-testid={testId}
      type={numeric ? 'number' : 'text'}
      value={value}
      placeholder={placeholder}
      list={list}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      onCommit={(text, el) => {
        /*
          A blank numeric cell restores the stored value and dispatches nothing.

          Without this, select-delete-retype — the most ordinary keystroke sequence in a spreadsheet-shaped
          editor — committed a zero on the way through, because `Number('')` is 0 and 0 !== 12. On the
          duration of a €25,000-a-month agreement that stored a term of zero and, until this milestone, drew
          the row as an empty track: label, grid lines, no mark at all. It is the same rule P4h-a settled for
          the assumptions grid — a blank scalar is not a zero — reopened in the register that feeds both of
          these charts. A text cell is different: emptying a service description IS the edit — and so is
          emptying a NULLABLE numeric one, which is why `blankIsNull` opts out: on the exit month blank is
          a stored state ("no month recorded"), not a value on the way to another one.
        */
        if (numeric && !blankIsNull && text.trim() === '') {
          el.value = value;
          return;
        }
        /*
          Compared the way the field means it: for a numeric cell "012" and "12" are the same number,
          and the original returns early on a no-op rather than logging one (`:6553`).

          A nullable cell needs the blank-ness compared too, not just the number. `Number('')` is 0, so
          typing 0 into an empty exit month reads as unchanged and commits nothing — leaving a cell
          showing 0 over a stored null. They price the same (both bill nothing), which is exactly why it
          would never be noticed; the register would simply be stating a decision nobody made.
        */
        const blankNow = text.trim() === '';
        const blankBefore = value.trim() === '';
        const changed = numeric
          ? (blankIsNull && blankNow !== blankBefore) || Number(text) !== Number(value)
          : text !== value;
        if (changed) onCommit(text);
      }}
    />
  );
}
